import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import type { editor as MonacoEditor } from 'monaco-editor';
import './CodeCompiler.css';
import { type ReactProblem } from '../data/problems';
import { runReactProblem, sendPreview, waitForHarnessReady, type ReactRunResult } from '../utils/reactRunner';
import SplitPane from './SplitPane';
import { useIsMobile } from '../lib/useIsMobile';
import { Markdown } from '../lib/markdown';

// Standalone IDE pane for React problems. Renders alongside the rest of
// the app (uses CodeCompiler.css for visual consistency) but routes through
// the iframe harness instead of Pyodide / runJsAlgorithm.
//
// Kept deliberately simpler than CodeCompiler.tsx — React problems are
// single-file for now (just the entry component), so there's no tab strip,
// no language switcher, no submit-progress flow yet. Those are follow-ups.

interface Props {
  problem: ReactProblem;
}

function entryFile(problem: ReactProblem) {
  const files = problem.files.javascript;
  return files.find(f => f.isEntry) ?? files[files.length - 1] ?? files[0];
}

const ReactProblemPane: React.FC<Props> = ({ problem }) => {
  const navigate = useNavigate();
  const entry = entryFile(problem);

  const [code, setCode] = useState<string>(entry?.code ?? '');
  const [running, setRunning] = useState(false);
  const [harnessReady, setHarnessReady] = useState(false);
  const [result, setResult] = useState<ReactRunResult | null>(null);
  // Preview tab state
  const [promptTab, setPromptTab] = useState<'prompt' | 'preview'>('prompt');
  const [previewError, setPreviewError] = useState<string | null>(null);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const editorRef = useRef<MonacoEditor.IStandaloneCodeEditor | null>(null);
  const previewTimerRef = useRef<number | null>(null);

  // Reset state on problem change (e.g. user navigates between React problems).
  useEffect(() => {
    setCode(entry?.code ?? '');
    setResult(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problem.id]);

  // Wait for the harness once mounted. waitForHarnessReady resolves when the
  // iframe fires `{type:'ready'}` — typically ~1-2s on a cold load (esm.sh +
  // Babel-standalone). If we miss the message (already fired before listener
  // registration), the 10s timeout will reject — at which point users can
  // still attempt to run and surface the contentWindow error.
  useEffect(() => {
    if (!iframeRef.current) return;
    let cancelled = false;
    iframeRef.current.addEventListener('load', () => {
      if (cancelled || !iframeRef.current) return;
      waitForHarnessReady(iframeRef.current, 15_000)
        .then(() => { if (!cancelled) setHarnessReady(true); })
        .catch(() => { /* ignore — the iframe may have unmounted */ });
    }, { once: true });
    return () => { cancelled = true; };
  }, []);

  // Debounced auto-preview: while the preview tab is active, re-render the
  // user's component 400 ms after their last keystroke. We don't preview
  // when the prompt tab is active (no point — the iframe is hidden).
  useEffect(() => {
    if (!harnessReady || promptTab !== 'preview' || !iframeRef.current) return;
    if (previewTimerRef.current !== null) window.clearTimeout(previewTimerRef.current);
    previewTimerRef.current = window.setTimeout(() => {
      if (!iframeRef.current) return;
      void sendPreview(iframeRef.current, code).then(r => {
        setPreviewError(r.error ?? null);
      });
    }, 400);
    return () => {
      if (previewTimerRef.current !== null) window.clearTimeout(previewTimerRef.current);
    };
  }, [code, promptTab, harnessReady]);

  // Immediate render on tab-switch so the user sees the latest component
  // without waiting 400 ms.
  useEffect(() => {
    if (!harnessReady || promptTab !== 'preview' || !iframeRef.current) return;
    void sendPreview(iframeRef.current, code).then(r => setPreviewError(r.error ?? null));
  // Intentionally omit `code` — keystroke updates go through the debounced effect above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptTab, harnessReady]);

  const doRun = useMemo(() => async () => {
    if (!iframeRef.current || running) return;
    setRunning(true);
    try {
      const r = await runReactProblem(iframeRef.current, code, problem.testScript);
      setResult(r);
    } catch (err) {
      setResult({
        passed: false,
        errors: [err instanceof Error ? err.message : String(err)],
        logs: [],
      });
    } finally {
      setRunning(false);
    }
  }, [code, problem.testScript, running]);

  const runDisabled = !harnessReady || running;
  const runLabel = !harnessReady ? '⌛ Loading harness…' : running ? '⏳ Running…' : '▶ Run';

  // On narrow viewports, prompt and editor stack vertically (prompt on top)
  // instead of sitting side-by-side — the horizontal split is unreadable
  // on a phone-width screen.
  const isMobile = useIsMobile();

  const promptPane = (
    <div className="ide-prompt" style={{ position: 'relative' }}>
      {/* Tab strip: prompt text vs live component preview */}
      <div style={{ display: 'flex', gap: 5, marginBottom: 8, flexShrink: 0 }}>
        {(['prompt', 'preview'] as const).map(t => (
          <button
            key={t}
            className={`sk-tab ${promptTab === t ? 'active' : ''}`}
            onClick={() => setPromptTab(t)}
            style={{ fontFamily: 'inherit', fontSize: 11, cursor: 'pointer' }}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Prompt content — hidden (not unmounted) when preview tab is active
          so scroll state and any text-selection survive tab toggles. */}
      <div style={{ display: promptTab === 'prompt' ? 'block' : 'none' }}>
        <Markdown className="ide-description" text={problem.description} />

        {problem.examples.length > 0 && (
          <>
            <div className="ide-anno-sub">Examples</div>
            {problem.examples.map((ex, i) => (
              <div key={i} className="ide-example">
                <div><span className="ide-label">Input:</span> <code>{ex.input}</code></div>
                <div><span className="ide-label">Output:</span> <code>{ex.output}</code></div>
                {ex.explanation && <div className="ide-explanation">{ex.explanation}</div>}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Preview — the harness iframe is reparented (visually) into this
          slot. The iframe element itself stays mounted at the parent IDE
          container so it doesn't get torn down + re-booted on tab switch. */}
      <div style={{
        display: promptTab === 'preview' ? 'flex' : 'none',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        gap: 6,
      }}>
        {previewError && (
          <div style={{
            padding: '6px 10px',
            background: 'var(--paper-2)',
            border: '1px dashed var(--w-coral)',
            color: 'var(--w-coral)',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 11,
            userSelect: 'text',
          }}>
            {previewError}
          </div>
        )}
        <iframe
          ref={iframeRef}
          title="React preview"
          src="/react.harness.html"
          style={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            border: '1.4px solid var(--ink)',
            background: 'white',
          }}
        />
        <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: 'var(--ink-faint)' }}>
          auto-renders 400ms after your last keystroke. switch back to prompt to read the spec.
        </div>
      </div>
    </div>
  );

  const editorPane = (
    <div className="ide-editor-col">
      <div className="ide-file-tabs">
        <span className="sk-tab active" style={{ fontSize: 11 }}>{entry?.name ?? 'Component.jsx'}</span>
        <span style={{ flex: 1 }} />
        <span className="sk-pill amber" style={{ fontSize: 10 }}>jsx</span>
        <button
          className="sk-btn ghost"
          style={{ fontSize: 11 }}
          onClick={() => setCode(entry?.code ?? '')}
          title="Discard your edits and restore the starter"
        >
          ↻ reset
        </button>
      </div>

      <div className="ide-editor-wrapper">
        <Editor
          height="100%"
          path={entry?.name ?? 'Component.jsx'}
          language="javascript"
          value={code}
          onChange={(val) => setCode(val ?? '')}
          theme="vs-dark"
          onMount={(editor) => { editorRef.current = editor; }}
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            tabSize: 2,
            scrollBeyondLastLine: false,
            fontFamily: '"JetBrains Mono", "Fira Code", monospace',
            lineNumbersMinChars: 3,
            padding: { top: 8 },
            // Auto-relayout on container resize. Needed on mobile where the
            // horizontal SplitPane (whose onResize call layout()) isn't rendered.
            automaticLayout: true,
          }}
        />
      </div>
    </div>
  );

  return (
    <div className="ide-container">
      {/* Sub-header — minimal version of the CodeCompiler one */}
      <div className="ide-subheader">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
          <span
            onClick={() => navigate('/')}
            style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: 'var(--ink-faint)', cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap' }}
          >
            map
          </span>
          <span style={{ color: 'var(--ink-faint)', fontSize: 10 }}>›</span>
          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>{problem.id} · {problem.title}</span>
          <span className={`sk-pill ${problem.difficulty === 'Easy' ? 'cream' : problem.difficulty === 'Medium' ? 'amber' : 'coral'}`} style={{ fontSize: 10 }}>
            {problem.difficulty.toLowerCase()}
          </span>
          <span className="sk-pill amber" style={{ fontSize: 10 }}>React</span>
        </div>
      </div>

      {/* Body + test pane in a vertical SplitPane; body itself is a horizontal
          SplitPane for prompt / editor. Mirrors CodeCompiler so resizing
          behaves consistently across both IDEs and the panes stay balanced
          on first render (instead of the prompt eating all the width). */}
      <SplitPane
        direction="vertical"
        storageKey="sb.split.react.test"
        defaultSize={180}
        anchor="second"
        min={100}
      >
        {isMobile ? (
          <div className="ide-body ide-body--stacked">
            {promptPane}
            {editorPane}
          </div>
        ) : (
          <div className="ide-body">
            <SplitPane
              direction="horizontal"
              storageKey="sb.split.react.body"
              defaultSize={420}
              anchor="first"
              min={240}
              onResize={() => editorRef.current?.layout()}
            >
              {promptPane}
              {editorPane}
            </SplitPane>
          </div>
        )}

      {/* Bottom: run + assertion-results pane — second child of the vertical SplitPane */}
      <div className="ide-test-pane" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            className="sk-btn primary"
            style={{ fontSize: 12 }}
            onClick={() => { void doRun(); }}
            disabled={runDisabled}
            title={!harnessReady ? 'Waiting for the React harness to finish loading…' : undefined}
          >
            {runLabel}
          </button>
          {result && (
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: result.passed ? 'var(--good)' : 'var(--w-coral)' }}>
              {result.passed
                ? '✓ all assertions passed'
                : `✗ ${result.errors.length} assertion(s) failed`}
            </span>
          )}
        </div>

        {result && !result.passed && (
          <ul style={{ margin: 0, paddingLeft: 18, fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'var(--w-coral)' }}>
            {result.errors.map((e, i) => (
              <li key={i} style={{ userSelect: 'text' }}>{e}</li>
            ))}
          </ul>
        )}

        {result && result.logs.length > 0 && (
          <details style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: 'var(--ink-soft)' }}>
            <summary style={{ cursor: 'pointer' }}>console ({result.logs.length} line{result.logs.length === 1 ? '' : 's'})</summary>
            <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
              {result.logs.map((l, i) => (
                <li key={i} style={{ color: l.kind === 'err' ? 'var(--w-coral)' : 'var(--ink-soft)' }}>
                  {l.msg}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
      </SplitPane>
    </div>
  );
};

export default ReactProblemPane;
